import "@app/App.css";
import { Button } from "@comps/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_FieldMenuItem, TW_FieldSection, TW_FieldSection_Label, TW_FieldUpdateName_Input, TW_UpdateFieldDialogContent, TW_UpdateFieldForm, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

interface FieldMenuDuplicateItemProps {
  field: Field;
  onFieldUpdate: (field: Field) => void;
  closeMenu: () => void;
}

function FieldMenuDuplicateItem({ field, onFieldUpdate, closeMenu }: FieldMenuDuplicateItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const hover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  const newField = (field: Field) => {
    onFieldUpdate(field);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDialogOpen(false);
    closeMenu();
  }

  function changeOpenState(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={changeOpenState}>
      <DialogTrigger className={TW_FieldMenuItem + ` ${field === null ? "item-disabled" : ""}`} {...hover}><Copy className="FieldMenuItemIcon" />{t("field.button.duplicate.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()} className={TW_UpdateFieldDialogContent}>
        <DialogHeader>
          <DialogTitle>{t("field.dialog.duplicate.title")}</DialogTitle>
          <DialogDescription hidden>{t("field.dialog.duplicate.description")}</DialogDescription>
        </DialogHeader>
        {field && <FieldDuplicateForm initialField={field} onNewField={newField} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface FieldDuplicateFormProps {
  initialField: Field;
  onNewField: (field: Field) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function FieldDuplicateForm({ initialField, onNewField, onCancel }: FieldDuplicateFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos)
  const [error, setError] = useState<string | null>(null);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const fetchData = useDataStore(state => state.fetchData);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    fieldName: z.string().min(1, {
      message: t("field.dialog.error.name.missing"),
    })
  })

  const form = useForm<z.infer<typeof formSchema>>({
    values: {
      fieldName: initialField.name + t("field.dialog.duplicate.suffix"),
    },
    resolver: zodResolver(formSchema),
  })

  function submitField(objectInfos: ObjectInfos, fieldToDuplicate: Field, name: string, order: number) {

    const body: Field = {
      _key: "",
      name: name,
      type: fieldToDuplicate.type,
      readOnly: fieldToDuplicate.readOnly,
      formula: fieldToDuplicate.formula,
      order: order,
      format: fieldToDuplicate.format,
      typeItems: fieldToDuplicate.typeItems,
      aggregation: fieldToDuplicate.aggregation,
    }

    post<Field>(`${BASE_URL}/model/workspaces/${workspace!._key}/objects/${objectInfos!.object._key}/fields`, body).then(newField => {
      if (isCompozzError(newField)) {
        const cError = newField as CompozzError;
        if (cError.i18n) {
          setError(translate(t, cError.i18n, cError.params));
        } else {
          setError(cError.message);
        }
      } else {
        setError(null);

        if (objectInfos) {
          const newFields = (objectInfos.fields)
            ? [...objectInfos.fields.filter(f => f.order < newField!.order), newField!, ...objectInfos.fields.filter(f => f.order >= newField!.order)]
            : [newField!];
          const newSelectedObjectInfos = { ...objectInfos, fields: newFields };
          updateSelectedObjectInfos(newSelectedObjectInfos as ObjectInfos, null, null, sharedObjectInfos);
        }
        fetchData();

        onNewField(newField as Field);
      }
    })

  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    setError(null);
    if (!workspace || !selectedObjectInfos) {
      console.error("No workspace or object selected");
      return;
    }
    const fields = selectedObjectInfos.fields;
    const initialFieldOrder = initialField.order;
    let nextFieldOrder: number | undefined = undefined;
    for (const field of fields) {
      if (field.name === initialField.name) {
        continue;
      }
      if ((nextFieldOrder === undefined && field.order >= initialFieldOrder) || (nextFieldOrder !== undefined && field.order >= initialFieldOrder && field.order < nextFieldOrder)) {
        nextFieldOrder = field.order;
      }
    }
    let newFieldOrder = initialFieldOrder + DEFAULT_ORDER_INCREMENT;
    if (nextFieldOrder !== undefined) {
      newFieldOrder = (nextFieldOrder + initialFieldOrder) / 2;
    }
    submitField(selectedObjectInfos!, initialField, values.fieldName, newFieldOrder);
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  function cancel(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onCancel(e);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={TW_UpdateFieldForm}>

        <div className={TW_FieldSection + " mt-4"}>
          <p className={TW_FieldSection_Label}>{t(`field.dialog.name.label`)}</p>
          <FormField
            control={form.control}
            name="fieldName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input className={TW_FieldUpdateName_Input} placeholder={t("field.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...confirmHover}>{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default FieldMenuDuplicateItem;
